/** FeedbackProvider — mounts the toast stack + confirm/prompt dialogs. The contexts and consumer hooks live in ./Feedback. */

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircleIcon, CheckCircle2Icon, InfoIcon, XIcon } from 'lucide-react'
import { planGateJustSurfaced } from '../../lib/plan-gate'
import { ToastContext, ConfirmContext, PromptContext, type ToastTone, type ToastEntry, type ToastContextValue, type ConfirmOptions, type ConfirmContextValue, type PromptOptions, type PromptContextValue } from './Feedback'

// ─── Provider ─────────────────────────────────────────────────────────────

type PendingConfirm = ConfirmOptions & {
  resolve: (value: boolean) => void
}

type PendingPrompt = PromptOptions & {
  resolve: (value: string | null) => void
}

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null)
  const [pendingPrompt, setPendingPrompt] = useState<PendingPrompt | null>(null)

  // Per-toast timers — keep refs so dismiss-by-user clears the pending close.
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const t = timersRef.current.get(id)
    if (t) {
      clearTimeout(t)
      timersRef.current.delete(id)
    }
  }, [])

  const notify = useCallback<ToastContextValue['notify']>(
    (message, opts = {}) => {
      // G-38c — a handler's generic error toast fired right after the shell
      // surfaced a 402 says the same thing worse ("Action failed"); drop it.
      if ((opts.tone ?? 'info') === 'error' && !opts.fromPlanGate && planGateJustSurfaced()) return
      const id = crypto.randomUUID()
      const entry: ToastEntry = {
        id,
        message,
        tone: opts.tone ?? 'info',
        action: opts.action,
      }
      setToasts((prev) => [...prev, entry])
      const ms = opts.durationMs ?? 4000
      if (ms > 0) {
        const timer = setTimeout(() => dismiss(id), ms)
        timersRef.current.set(id, timer)
      }
    },
    [dismiss],
  )

  const confirm = useCallback<ConfirmContextValue['confirm']>(
    (opts) =>
      new Promise<boolean>((resolve) => {
        setPendingConfirm({ ...opts, resolve })
      }),
    [],
  )

  const closeConfirm = useCallback(
    (result: boolean) => {
      pendingConfirm?.resolve(result)
      setPendingConfirm(null)
    },
    [pendingConfirm],
  )

  const prompt = useCallback<PromptContextValue['prompt']>(
    (opts) =>
      new Promise<string | null>((resolve) => {
        setPendingPrompt({ ...opts, resolve })
      }),
    [],
  )

  const closePrompt = useCallback(
    (result: string | null) => {
      pendingPrompt?.resolve(result)
      setPendingPrompt(null)
    },
    [pendingPrompt],
  )

  const toastCtx = useMemo(() => ({ notify }), [notify])
  const confirmCtx = useMemo(() => ({ confirm }), [confirm])
  const promptCtx = useMemo(() => ({ prompt }), [prompt])

  return (
    <ToastContext.Provider value={toastCtx}>
      <ConfirmContext.Provider value={confirmCtx}>
        <PromptContext.Provider value={promptCtx}>
          {children}
          <ToastStack toasts={toasts} onDismiss={dismiss} />
          {pendingConfirm && (
            <ConfirmDialog
              options={pendingConfirm}
              onResolve={closeConfirm}
            />
          )}
          {pendingPrompt && (
            <PromptDialog
              options={pendingPrompt}
              onResolve={closePrompt}
            />
          )}
        </PromptContext.Provider>
      </ConfirmContext.Provider>
    </ToastContext.Provider>
  )
}

// ─── Toast UI ─────────────────────────────────────────────────────────────

function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: ToastEntry[]
  onDismiss: (id: string) => void
}) {
  if (toasts.length === 0) return null
  return (
    <div
      // Above SlideOver (120) and ModalShell (200) — sequence "Preview and test"
      // toasts were invisible while the Email panel stayed open.
      className="pointer-events-none fixed bottom-4 right-4 z-[300] flex w-full max-w-sm flex-col gap-2"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  )
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastEntry
  onDismiss: () => void
}) {
  const palette: Record<ToastTone, string> = {
    success: 'border-green-200 bg-green-50 text-green-800',
    info: 'border-stone-200 bg-white text-stone-800',
    error: 'border-red-200 bg-red-50 text-red-800',
  }
  const Icon =
    toast.tone === 'success' ? CheckCircle2Icon : toast.tone === 'error' ? AlertCircleIcon : InfoIcon
  return (
    <div
      role="status"
      className={`pointer-events-auto flex items-start gap-2 rounded-lg border p-3 shadow-md ${palette[toast.tone]}`}
    >
      <Icon size={16} className="mt-0.5 flex-shrink-0" />
      <div className="flex-1 text-sm">{toast.message}</div>
      {toast.action && (
        <button
          type="button"
          onClick={() => {
            toast.action?.onClick()
            onDismiss()
          }}
          className="flex-shrink-0 rounded px-2 py-0.5 text-xs font-medium underline hover:no-underline"
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="flex-shrink-0 rounded p-0.5 opacity-60 hover:opacity-100"
      >
        <XIcon size={14} />
      </button>
    </div>
  )
}

// ─── Confirm UI ───────────────────────────────────────────────────────────

function ConfirmDialog({
  options,
  onResolve,
}: {
  options: ConfirmOptions
  onResolve: (result: boolean) => void
}) {
  const confirmLabel = options.confirmLabel ?? (options.danger ? 'Delete' : 'Confirm')
  const cancelLabel = options.cancelLabel ?? 'Cancel'
  const confirmTone = options.danger
    ? 'bg-red-600 hover:bg-red-700'
    : 'bg-blue-600 hover:bg-blue-700'

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      className="fixed inset-0 z-[210] flex items-center justify-center vp-modal-scrim p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onResolve(false)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onResolve(false)
        // Buttons already fire their own click on Enter — resolving here too
        // would turn Enter-on-Cancel into a confirm (fatal for danger dialogs).
        if (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement)) onResolve(true)
      }}
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl ring-1 ring-stone-200">
        <h2 id="confirm-title" className="text-sm font-semibold text-stone-900">
          {options.title}
        </h2>
        <p className="mt-2 text-sm text-stone-600">{options.message}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onResolve(false)}
            className="rounded border border-stone-300 px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            autoFocus
            onClick={() => onResolve(true)}
            className={`rounded px-3 py-1.5 text-sm font-medium text-white ${confirmTone}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ─── Prompt UI ────────────────────────────────────────────────────────────

function PromptDialog({
  options,
  onResolve,
}: {
  options: PromptOptions
  onResolve: (result: string | null) => void
}) {
  const [value, setValue] = useState(options.defaultValue ?? '')
  const [error, setError] = useState<string | null>(null)
  const confirmLabel = options.confirmLabel ?? 'OK'
  const cancelLabel = options.cancelLabel ?? 'Cancel'

  const submit = () => {
    const trimmed = value.trim()
    if (options.required && trimmed.length === 0) {
      setError('A value is required.')
      return
    }
    if (options.validate) {
      const err = options.validate(value)
      if (err) {
        setError(err)
        return
      }
    }
    onResolve(value)
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="prompt-title"
      className="fixed inset-0 z-[210] flex items-center justify-center vp-modal-scrim p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onResolve(null)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onResolve(null)
        // Same guard as ConfirmDialog: Enter on the Cancel button must not submit.
        if (e.key === 'Enter' && !options.multiline && !(e.target instanceof HTMLButtonElement)) submit()
      }}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl ring-1 ring-stone-200">
        <h2 id="prompt-title" className="text-sm font-semibold text-stone-900">
          {options.title}
        </h2>
        {options.message && (
          <p className="mt-1 text-xs text-stone-600">{options.message}</p>
        )}
        <div className="mt-3">
          {options.multiline ? (
            <textarea
              autoFocus
              value={value}
              onChange={(e) => {
                setValue(e.target.value)
                if (error) setError(null)
              }}
              placeholder={options.placeholder}
              rows={3}
              className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none focus:ring-1 focus:ring-stone-500"
            />
          ) : (
            <input
              autoFocus
              type="text"
              value={value}
              onChange={(e) => {
                setValue(e.target.value)
                if (error) setError(null)
              }}
              placeholder={options.placeholder}
              className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none focus:ring-1 focus:ring-stone-500"
            />
          )}
          {error && (
            <p className="mt-1 text-xs text-red-600">{error}</p>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onResolve(null)}
            className="rounded border border-stone-300 px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={submit}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
